import { AuditActions, type AuditAction } from '@verydia/contracts';

/**
 * How an audit action reads to a person, and whether it belongs on the Overview.
 *
 * WHY THIS IS A CLOSED REGISTRY
 *
 * The previous version transformed the wire value at render time —
 * `PRODUCT_ENVIRONMENT_CREATED` became "Product environment created" by lowercasing
 * and swapping underscores. That works until it does not: a new action ships and
 * appears in production copy nobody wrote, phrased by a regular expression.
 *
 * Every action is listed here explicitly. Adding one to the domain without
 * deciding how it reads, and whether an operator should see it, fails to compile.
 *
 * WHY SOME ARE HIDDEN
 *
 * Recent Activity is a curated summary; the Audit Log is the complete record.
 * Platform-internal events — bootstrapping, first sign-in — are real history and
 * stay in the log, but they answer a support question rather than "what changed
 * in my catalog", so they do not compete for six rows on the Overview.
 *
 * Hidden is a presentation decision. Nothing here removes or rewrites an audit
 * record; that would be falsifying the thing the log exists to be.
 */
export interface ActivityPresentation {
  /** Sentence case, written out. Never a transformed wire value. */
  readonly label: string;
  /** Whether an organization operator should see it on the Overview. */
  readonly visible: boolean;
}

export const ACTIVITY_PRESENTATION = {
  // Platform internals: kept in the Audit Log, not surfaced here.
  [AuditActions.PLATFORM_OWNER_BOOTSTRAPPED]: {
    label: 'Platform owner bootstrapped',
    visible: false,
  },
  [AuditActions.USER_FIRST_SEEN]: { label: 'User signed in for the first time', visible: false },
  [AuditActions.USER_SUSPENDED]: { label: 'User suspended', visible: false },
  [AuditActions.USER_REACTIVATED]: { label: 'User reactivated', visible: false },
  [AuditActions.ORGANIZATION_OWNER_SEEDED]: { label: 'Organization owner seeded', visible: false },

  // The organization itself.
  [AuditActions.VENDOR_ORGANIZATION_CREATED]: { label: 'Organization created', visible: true },
  [AuditActions.VENDOR_ORGANIZATION_UPDATED]: { label: 'Organization updated', visible: true },

  // Team.
  [AuditActions.ORGANIZATION_INVITATION_CREATED]: { label: 'Invitation sent', visible: true },
  [AuditActions.ORGANIZATION_INVITATION_REVOKED]: { label: 'Invitation revoked', visible: true },
  [AuditActions.ORGANIZATION_MEMBERSHIP_ACTIVATED]: { label: 'Member joined', visible: true },
  [AuditActions.ORGANIZATION_MEMBER_ROLE_CHANGED]: { label: 'Member role changed', visible: true },
  [AuditActions.ORGANIZATION_MEMBER_SUSPENDED]: { label: 'Member suspended', visible: true },
  [AuditActions.ORGANIZATION_MEMBER_REMOVED]: { label: 'Member removed', visible: true },

  // Catalog.
  [AuditActions.PRODUCT_CREATED]: { label: 'Product created', visible: true },
  [AuditActions.PRODUCT_UPDATED]: { label: 'Product updated', visible: true },
  [AuditActions.PRODUCT_ARCHIVED]: { label: 'Product archived', visible: true },
  [AuditActions.PRODUCT_ENVIRONMENT_CREATED]: { label: 'Environment created', visible: true },
  [AuditActions.PRODUCT_ENVIRONMENT_UPDATED]: { label: 'Environment updated', visible: true },
  [AuditActions.PRODUCT_ENVIRONMENT_ARCHIVED]: { label: 'Environment archived', visible: true },
  [AuditActions.LICENSED_PRODUCT_PLAN_CREATED]: {
    label: 'Licensed product plan created',
    visible: true,
  },
  [AuditActions.LICENSED_PRODUCT_PLAN_UPDATED]: {
    label: 'Licensed product plan updated',
    visible: true,
  },
  [AuditActions.LICENSED_PRODUCT_PLAN_ACTIVATED]: {
    label: 'Licensed product plan published',
    visible: true,
  },
  [AuditActions.LICENSED_PRODUCT_PLAN_ARCHIVED]: {
    label: 'Licensed product plan archived',
    visible: true,
  },
  // Customers.
  [AuditActions.CUSTOMER_CREATED]: { label: 'Customer created', visible: true },
  [AuditActions.CUSTOMER_UPDATED]: { label: 'Customer updated', visible: true },
  [AuditActions.CUSTOMER_ARCHIVED]: { label: 'Customer archived', visible: true },
  // The `satisfies` is the guard: a new AuditAction with no entry here is a
  // compile error, not a raw enum leaking into the console.
} satisfies Record<AuditAction, ActivityPresentation>;

/**
 * Presentation for an action, or `null` if this build does not know it.
 *
 * `null` rather than a humanised fallback. A server one version ahead can emit an
 * action this console has never heard of, and inventing a label for it would put
 * machine text in front of an operator. Skipping the row is honest; the Audit Log
 * still has it.
 */
export function activityPresentation(action: string): ActivityPresentation | null {
  return (ACTIVITY_PRESENTATION as Record<string, ActivityPresentation>)[action] ?? null;
}

/** Whether this action belongs in the Overview's curated recent window. */
export function isVisibleActivity(action: string): boolean {
  return activityPresentation(action)?.visible ?? false;
}
